import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { $getRoot, LexicalEditor, LexicalNode } from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ActionIcon, Box, Flex } from '@mantine/core';
import { IconSeparatorHorizontal } from '@tabler/icons-react';
import { $createPageBreakNode } from '../nodes/PageBreakNode';

const GAP_DETECTION_PADDING = 8;

type GapInfo = {
  insertAfterNode: LexicalNode | null;
  lineY: number;
};

function findGapAtY(
  editor: LexicalEditor,
  mouseY: number,
  anchorElem: HTMLElement,
): GapInfo | null {
  let result: GapInfo | null = null;

  editor.getEditorState().read(() => {
    const root = $getRoot();
    const children = root.getChildren();

    if (children.length === 0) return;

    const anchorRect = anchorElem.getBoundingClientRect();

    for (let i = 0; i < children.length - 1; i++) {
      const currentElem = editor.getElementByKey(children[i].getKey());
      const nextElem = editor.getElementByKey(children[i + 1].getKey());

      if (!currentElem || !nextElem) continue;

      const currentRect = currentElem.getBoundingClientRect();
      const nextRect = nextElem.getBoundingClientRect();

      const gapTop = currentRect.bottom;
      const gapBottom = nextRect.top;

      if (mouseY >= gapTop - GAP_DETECTION_PADDING && mouseY <= gapBottom + GAP_DETECTION_PADDING) {
        result = {
          insertAfterNode: children[i],
          lineY: (gapTop + gapBottom) / 2 - anchorRect.top,
        };
        return;
      }
    }
  });

  return result;
}

function InsertionLine({ lineY, onClick }: { lineY: number; onClick: () => void }) {
  return (
    <Box
      data-insert-element-line
      pos="absolute"
      top={lineY}
      left={0}
      right={0}
      py="xs"
      style={{
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
      }}
    >
      <Flex
        align="center"
        gap="xs"
        px="xs"
        style={{
          opacity: 0.6,
          transition: 'opacity 0.15s ease',
        }}
        className="insert-line-inner"
      >
        <Box flex={1} style={{ borderBottom: '1px solid var(--mantine-color-gray-1)' }} />
        <ActionIcon
          variant="subtle"
          color="gray.5"
          size="sm"
          onClick={onClick}
          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        >
          <IconSeparatorHorizontal size={16} />
        </ActionIcon>
        <Box flex={1} style={{ borderBottom: '1px solid var(--mantine-color-gray-1)' }} />
      </Flex>
      <style>{`
        [data-insert-element-line]:hover .insert-line-inner {
          opacity: 1 !important;
        }
      `}</style>
    </Box>
  );
}

function InsertElementContainer({ anchorElem }: { anchorElem: HTMLElement }) {
  const [editor] = useLexicalComposerContext();
  const [gapInfo, setGapInfo] = useState<GapInfo | null>(null);
  const insertAfterNodeRef = useRef<LexicalNode | null>(null);

  const insertPageBreak = useCallback(() => {
    const insertAfterNode = insertAfterNodeRef.current;
    editor.update(() => {
      const pageBreakNode = $createPageBreakNode();

      if (insertAfterNode === null) {
        const root = $getRoot();
        const firstChild = root.getFirstChild();
        if (firstChild) {
          firstChild.insertBefore(pageBreakNode);
        } else {
          root.append(pageBreakNode);
        }
      } else {
        insertAfterNode.insertAfter(pageBreakNode);
      }

      pageBreakNode.selectEnd();
    });
    setGapInfo(null);
  }, [editor]);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-insert-element-line]')) return;

      const gap = findGapAtY(editor, event.clientY, anchorElem);

      if (gap) {
        insertAfterNodeRef.current = gap.insertAfterNode;
        setGapInfo(gap);
      } else {
        setGapInfo(null);
      }
    },
    [editor, anchorElem],
  );

  const handleMouseLeave = useCallback((event: MouseEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget?.closest('[data-insert-element-line]')) return;
    setGapInfo(null);
  }, []);

  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    rootElement.addEventListener('mousemove', handleMouseMove);
    rootElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      rootElement.removeEventListener('mousemove', handleMouseMove);
      rootElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [editor, handleMouseMove, handleMouseLeave]);

  if (!gapInfo) return null;

  return <InsertionLine lineY={gapInfo.lineY} onClick={insertPageBreak} />;
}

export default function InsertElementPlugin({ anchorElem }: { anchorElem: HTMLElement }) {
  const isEditable = useLexicalEditable();

  return isEditable
    ? createPortal(<InsertElementContainer anchorElem={anchorElem} />, anchorElem)
    : null;
}
